import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { souserService } from "@/features/sending-org/services/souserService";
import { Input } from "@/shared/components/ui/Input";
import { Button } from "@/shared/components/ui/Button";
import { Spinner } from "@/shared/components/ui/Spinner";

export const SouserProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData, userToken, streamToken, roles, login } = useAuth();

  const [loading, setLoading]           = useState(true);
  const [firstName, setFirstName]       = useState("");
  const [lastName, setLastName]         = useState("");
  const [displayName, setDisplayName]   = useState("");
  const [positionTitle, setPositionTitle] = useState("");

  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState(false);

  const loadProfile = useCallback(async () => {
    try {
      const profile = await souserService.getMe();
      setFirstName(profile.first_name);
      setLastName(profile.last_name);
      setDisplayName(profile.display_name ?? "");
      setPositionTitle(profile.position_title ?? "");
    } catch {
      setError(t("souser.load_error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const handleSave = async () => {
    setError("");
    setSuccess(false);

    if (!firstName.trim()) { setError(t("souser.validation_first_name")); return; }
    if (!lastName.trim())  { setError(t("souser.validation_last_name")); return; }

    setSaving(true);
    try {
      const { record } = await souserService.updateMe({
        first_name:     firstName.trim(),
        last_name:      lastName.trim(),
        display_name:   displayName.trim() || null,
        position_title: positionTitle.trim() || null,
      });

      if (userData && userToken) {
        login(userToken, streamToken ?? "", {
          ...userData,
          firstName: record.first_name,
          lastName:  record.last_name,
        }, roles);
      }

      setSuccess(true);
    } catch {
      setError(t("souser.my_profile_save_error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg">
      <button
        type="button"
        onClick={() => navigate("/settings")}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-5"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        {t("common.back")}
      </button>

      <h1 className="text-xl font-bold text-gray-900 mb-5">{t("souser.my_profile")}</h1>

      {loading ? (
        <div className="py-10"><Spinner /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t("souser.field_first_name")}
              placeholder={t("souser.field_first_name_placeholder")}
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Input
              label={t("souser.field_last_name")}
              placeholder={t("souser.field_last_name_placeholder")}
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>

          <Input
            label={t("souser.field_display_name")}
            placeholder={t("souser.field_display_name_placeholder")}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />

          <Input
            label={t("souser.field_position")}
            placeholder={t("souser.field_position_placeholder")}
            value={positionTitle}
            onChange={(e) => setPositionTitle(e.target.value)}
          />

          {error   && <p className="text-sm text-red-600">{error}</p>}
          {success && <p className="text-sm text-green-600">{t("souser.my_profile_save_success")}</p>}

          <div className="pt-1">
            <Button onClick={handleSave} loading={saving} className="w-full sm:w-auto">
              {t("common.save")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
